import { APP_CHAIN } from '@/client/confs/app';
import { ALLOWANCE_QUERY } from '@/client/hooks/use-signed-allowance';
import { useWallet } from '@/client/hooks/use-wallet';
import { isActiveTransaction, useTransactionStore } from '@/client/store/transaction-store';
import { publicClient } from '@/client/utils/chain-client';
import { refetchQuery, refetchSignedUserAssets, refetchUserPositions } from '@/client/utils/query-status';
import { assertSuccessfulReceipt } from '@/client/utils/transaction-result';
import { USER_HOLDING_POSITIONS_QUERY } from '@/libs/react-sdk/api/use-user-holding-positions';
import { useEffect } from 'react';
import type { Hash, TransactionReceipt } from 'viem';

const watchers = new Map<string, Promise<TransactionReceipt>>();
const supersede = new Map<string, () => void>();
const generations = new Map<string, number>();
const consumed = new Set<string>();
const refreshed = new Set<string>();
export const consumeTransactionSuccess = (id: string): boolean => {
  if (consumed.has(id) || useTransactionStore.getState().records[id]?.stage !== 'confirmed') return false;
  consumed.add(id);
  return true;
};

/** One observer survives drawer unmounts; recovery only reads chain state. */
export const watchTransaction = (id: string, hash?: Hash, force = false): Promise<TransactionReceipt> => {
  const store = useTransactionStore.getState();
  if (hash) store.update(id, { hash, stage: 'pending', approvalConfirmed: false });
  const record = useTransactionStore.getState().records[id];
  if (!record?.hash || !isActiveTransaction(record)) return Promise.reject(new Error('transaction-unknown'));
  const key = id;
  const existing = watchers.get(key);
  if (existing && !force) return existing;
  if (force) supersede.get(id)?.();
  const generation = (generations.get(id) ?? 0) + 1;
  generations.set(id, generation);
  const timer = setTimeout(() => {
    const current = useTransactionStore.getState().records[id];
    if (generations.get(id) === generation && current?.stage === 'pending')
      useTransactionStore.getState().update(id, { stage: 'unknown' });
  }, 120_000);
  const query = (async () => {
    try {
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: record.hash!,
        onReplaced: replacement => {
          if (generations.get(id) !== generation) return;
          useTransactionStore.getState().update(id, {
            hash: replacement.transaction.hash,
            stage:
              replacement.reason === 'cancelled' ? 'cancelled' : replacement.reason === 'replaced' ? 'unknown' : 'pending',
            replacementChanged: replacement.reason === 'replaced' || record.replacementChanged,
          });
        },
      });
      if (generations.get(id) !== generation) throw new Error('transaction-superseded');
      const current = useTransactionStore.getState().records[id];
      if (current?.stage === 'cancelled') throw new Error('transaction-cancelled');
      if (current?.replacementChanged) {
        // A mined replacement consumes the original nonce; the original call cannot execute.
        useTransactionStore.getState().update(id, { stage: 'cancelled' });
        throw new Error('transaction-replaced');
      }
      try {
        assertSuccessfulReceipt(receipt);
      } catch (error) {
        useTransactionStore.getState().update(id, { stage: 'failed' });
        throw error;
      }
      useTransactionStore
        .getState()
        .update(
          id,
          record.step === 'approve'
            ? { step: 'execute', stage: 'wallet', hash: undefined, approvalConfirmed: true }
            : { stage: 'confirmed' }
        );
      return receipt;
    } catch (error) {
      if (generations.get(id) !== generation) throw new Error('transaction-superseded');
      const current = useTransactionStore.getState().records[id];
      if (current && isActiveTransaction(current) && current.hash) {
        useTransactionStore.getState().update(id, { stage: 'unknown' });
      }
      if (current && isActiveTransaction(current) && !current.replacementChanged) throw new Error('transaction-unknown');
      throw error;
    } finally {
      clearTimeout(timer);
      if (generations.get(id) === generation) {
        watchers.delete(key);
        supersede.delete(key);
      }
    }
  })();
  const promise = Promise.race([
    query,
    new Promise<TransactionReceipt>((_resolve, reject) => {
      supersede.set(id, () => reject(new Error('transaction-superseded')));
    }),
  ]);
  watchers.set(key, promise);
  return promise;
};

export const useTransactionRecovery = () => {
  const { walletClient } = useWallet();
  const account = walletClient?.account?.address;
  const chainId = walletClient?.chain?.id;
  const records = useTransactionStore(state => state.records);
  useEffect(() => {
    if (!account || chainId !== APP_CHAIN.id) return;
    const refresh = () =>
      Promise.allSettled([
        refetchUserPositions(),
        refetchSignedUserAssets(),
        refetchQuery('UserAssetsInfo'),
        refetchQuery(USER_HOLDING_POSITIONS_QUERY),
        refetchQuery(ALLOWANCE_QUERY),
        // the header wallet balance polls only every 60 s
        refetchQuery('SingerTokenBalance'),
      ]);
    for (const record of Object.values(records)) {
      if (record.account.toLowerCase() !== account.toLowerCase() || record.chainId !== chainId) continue;
      if (record.stage === 'pending' && record.hash) void watchTransaction(record.id).catch(() => {});
      if (record.stage === 'confirmed' && !refreshed.has(record.id)) {
        refreshed.add(record.id);
        void refresh();
      }
    }
  }, [records, account, chainId]);
};
